/**
 * Effective bench runs — one derivation, three consumers.
 *
 * A stored `BenchRun` describes what the user asked for. What actually gets
 * built is that run **clipped clear of the heater**, because a lav ledger
 * cannot pass through a stove. The renderer has always done this
 * (`clipBenchSpanToHeater` in detailHelpers.ts); validation, capacity and the
 * BOM must ask the same question or they will disagree with the model.
 *
 * Clipping deliberately happens HERE and not in `reconcileInteriorLayout`:
 * reconciliation rewrites stored state, and baking a clip into it would leave
 * a permanently shortened run behind after the user moves the heater away.
 */
import {
	BENCH_HEATER_CLIP_CLEARANCE_MM,
	MIN_BENCH_RUN_LENGTH_MM,
} from "./constants";
import {
	type BenchRun,
	benchRunFootprint,
	type FootprintMm,
	type HeaterSizeMm,
	heaterFootprint,
	type InteriorLayout,
	type InteriorRoom,
	wallAxisOriginMm,
	wallRunsAlongX,
} from "./types";

export interface EffectiveBenchRun {
	/** The run as built: same wall/depth, possibly a shorter span. */
	effective: BenchRun;
	footprint: FootprintMm;
	/** True when the heater forced the span shorter than the user asked for. */
	clipped: boolean;
}

interface Span {
	min: number;
	max: number;
}

/** Split a footprint into the run's own two axes: along its wall, and out from it. */
function spansFor(
	wall: BenchRun["wall"],
	box: FootprintMm,
): { along: Span; across: Span } {
	const x: Span = { min: box.minXMm, max: box.maxXMm };
	const z: Span = { min: box.minZMm, max: box.maxZMm };
	return wallRunsAlongX(wall)
		? { along: x, across: z }
		: { along: z, across: x };
}

/** Do two spans come within `clearance` of each other? */
function spansMeet(a: Span, b: Span, clearanceMm: number): boolean {
	return a.min < b.max + clearanceMm && a.max > b.min - clearanceMm;
}

/**
 * Shorten a run so it stops clear of the heater. Returns the run unchanged
 * when they never meet, a shortened run when they do, and `null` when what
 * survives is too short to be a bench — the same three outcomes as
 * `clipBenchSpanToHeater`.
 */
export function clipRunToHeater(
	run: BenchRun,
	room: InteriorRoom,
	heaterBox: FootprintMm | null,
): BenchRun | null {
	const clearanceMm = BENCH_HEATER_CLIP_CLEARANCE_MM;
	if (!heaterBox) return run;

	const bench = spansFor(run.wall, benchRunFootprint(run, room));
	const heater = spansFor(run.wall, heaterBox);
	if (
		!spansMeet(bench.across, heater.across, clearanceMm) ||
		!spansMeet(bench.along, heater.along, clearanceMm)
	) {
		return run;
	}

	// Keep the longer surviving side: cut back to the heater's near face on
	// whichever side of the heater the run's centre sits.
	const heaterCenter = (heater.along.min + heater.along.max) / 2;
	const runCenter = (bench.along.min + bench.along.max) / 2;
	const clipped =
		heaterCenter >= runCenter
			? { min: bench.along.min, max: heater.along.min - clearanceMm }
			: { min: heater.along.max + clearanceMm, max: bench.along.max };

	const lengthMm = clipped.max - clipped.min;
	if (lengthMm < MIN_BENCH_RUN_LENGTH_MM) return null;

	return {
		...run,
		startMm: clipped.min - wallAxisOriginMm(room, run.wall),
		lengthMm,
	};
}

export interface EffectiveLayoutGeometry {
	runs: EffectiveBenchRun[];
	/**
	 * Runs the heater left no usable span for. Nothing is drawn and nothing is
	 * bought for these — they are the one bench/heater conflict a user cannot
	 * see in the model, so validation reports them.
	 */
	dropped: BenchRun[];
	/** Null when the caller gave no heater extents (e.g. a room without one). */
	heaterBox: FootprintMm | null;
}

/**
 * Resolve a layout into the geometry that is actually built: heater box plus
 * heater-clipped bench runs, restricted to one room.
 */
export function resolveEffectiveLayout(
	layout: InteriorLayout,
	room: InteriorRoom,
	heaterSize?: HeaterSizeMm | null,
): EffectiveLayoutGeometry {
	const heaterBox = heaterSize
		? heaterFootprint(layout.heater, room, heaterSize)
		: null;

	const runs: EffectiveBenchRun[] = [];
	const dropped: BenchRun[] = [];
	for (const run of layout.benchRuns) {
		if (run.room !== room.id) continue;
		const effective = clipRunToHeater(run, room, heaterBox);
		if (!effective) {
			dropped.push(run);
			continue;
		}
		runs.push({
			effective,
			footprint: benchRunFootprint(effective, room),
			clipped: effective.lengthMm !== run.lengthMm,
		});
	}

	return { runs, dropped, heaterBox };
}
